import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
// Material icons
import HomeIcon from '@mui/icons-material/Home';
import ExtensionIcon from '@mui/icons-material/Extension';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import TopicIcon from '@mui/icons-material/Topic';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import GroupIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import CodeIcon from '@mui/icons-material/Code';
// Progress icons 
import LogoFull from './LogoFull';
import ProgressLogoIcon from './ProgressLogoIcon';

import { ProfileSidebarItem } from './ProfileSidebarItem';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
  //SidebarSubmenu,
  //SidebarSubmenuItem,
} from '@backstage/core-components';

import { FeatureFlagged } from '@backstage/core-app-api';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <ProgressLogoIcon />}
      </Link>
    </div>
  );
};

import CategoryIcon from '@mui/icons-material/Category';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';

//<SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
//<SidebarItem icon={CreateComponentIcon} to="catalog-import" text="Import..." />
//<SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Home" />


        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog"/>
        {/* Replace if we want the advanced sub menu
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog">
          
          <SidebarSubmenu title="Catalog">
            <SidebarSubmenuItem
              title="Domains"
              to="catalog?filters[kind]=domain"
              icon={useApp().getSystemIcon('kind:domain')}
            />
            <SidebarSubmenuItem
              title="Systems"
              to="catalog?filters[kind]=system"
              icon={useApp().getSystemIcon('kind:system')}
            />
            <SidebarSubmenuItem
              title="Components"
              to="catalog?filters[kind]=component"
              icon={useApp().getSystemIcon('kind:component')}
            />
            <SidebarSubmenuItem
              title="APIs"
              to="catalog?filters[kind]=api"
              icon={useApp().getSystemIcon('kind:api')}
            />
            <SidebarDivider />
            <SidebarSubmenuItem
              title="Resources"
              to="catalog?filters[kind]=resource"
              icon={useApp().getSystemIcon('kind:resource')}
            />
            <SidebarDivider />
            <SidebarSubmenuItem
              title="Groups"
              to="catalog?filters[kind]=group"
              icon={useApp().getSystemIcon('kind:group')}
            />
            <SidebarSubmenuItem
              title="Users"
              to="catalog?filters[kind]=user"
              icon={useApp().getSystemIcon('kind:user')}
            />
          </SidebarSubmenu>
        </SidebarItem>
          
        */}

        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />

        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />


        {/* End global nav */}

        <SidebarDivider />
        {/*
        <SidebarItem
          to =""
          icon={InfoIcon} // An icon to represent "Developer Docs" when collapsed
          text="Developer Docs" // The text to show when expanded
        />
        */}
        <SidebarItem icon={TopicIcon} to="docs/default/component/i360-dev-patterns" text="Patterns" />
        <SidebarItem icon={TopicIcon} to="docs/default/component/i360-dev-policies" text="Policies" />
        <SidebarItem icon={TopicIcon} to="docs/default/component/i360-dev-standards" text="Standards" />
        <SidebarItem icon={FolderSpecialIcon} to="docs" text="All Techdocs" />
        
        <SidebarDivider />


        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}

          <FeatureFlagged with="specreq-plugin-enabled">
            <SidebarItem icon={ExtensionIcon} to="specreq" text="Specreqs" />
          </FeatureFlagged>
          
          <FeatureFlagged with="announcements-plugin-enabled">
            <SidebarItem icon={TipsAndUpdatesIcon} to="announcements" text="Announcements" />
          </FeatureFlagged>

          <NotificationsSidebarItem />
          
          <ProfileSidebarItem />

          <FeatureFlagged with="validator-plugin-enabled">
            <SidebarItem icon={BuildIcon} to="entity-validation" text="Validator" />
          </FeatureFlagged>

          
          <RequirePermission
            permission={devToolsAdministerPermission}
            errorPage={<></>}>
            <SidebarItem icon={CodeIcon} to="devtools" text="DevTools" />
          </RequirePermission>

          <SidebarItem icon={DeviceHubIcon} to="visualizer" text="Visualizer" />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
